import { faCircleInfo } from '@fortawesome/pro-solid-svg-icons';
import { Code, Icon, Text, Heading, DataTable, Flex } from '@scale/scaleui-radix';
import { createColumnHelper } from '@tanstack/react-table';
import { Column } from '@scale/compass-shared/types';
import React, { useMemo } from 'react';

const columnHelper = createColumnHelper<{ header: string; value: string | JSX.Element }>();

const columns = [
  columnHelper.display({
    id: 'rowSelection',
    size: 36,
    enableResizing: false,
    enableHiding: false,
    header: function SelectColumnHeader() {
      return (
        <Flex align="center" height="100%" justify="center" width="100%">
          <DataTable.ColumnHeaderSelect />
        </Flex>
      );
    },
    cell: function SelectColumnCell({ row }) {
      return (
        <Flex align="center" height="100%" justify="center" width="100%">
          <DataTable.RowSelect row={row} />
        </Flex>
      );
    },
  }),
  columnHelper.accessor('header', {
    id: 'columnName',
    header: 'Column Name',
    cell: info => info.getValue(),
  }),
  columnHelper.accessor('value', {
    id: 'sampleValue',
    header: 'Sample Value',
    cell: info => info.getValue(),
  }),
];

interface MetadataPreviewProps {
  sampleTaskId: string;
  tableData: { header: string; value: string | JSX.Element }[];
  metadataColumns: Column[];
  isLoading: boolean;
  selectedColumns: Column[];
  setSelectedColumns: (columns: Column[]) => void;
}

export const MetadataPreview = ({
  sampleTaskId,
  tableData,
  isLoading,
  metadataColumns,
  selectedColumns,
  setSelectedColumns,
}: MetadataPreviewProps) => {
  // Compute currentRowSelection only when selectedColumns changes
  const currentRowSelection = useMemo(() => {
    return tableData.reduce((acc, row, index) => {
      acc[index] = selectedColumns.some(selected => selected.header === row.header);
      return acc;
    }, {} as Record<string, boolean>);
  }, [selectedColumns, tableData]);

  const handleSelectionChange = (
    newSelection:
      | Record<string, boolean>
      | ((old: Record<string, boolean>) => Record<string, boolean>),
  ) => {
    const finalSelection =
      typeof newSelection === 'function' ? newSelection(currentRowSelection) : newSelection;

    const newSelectedColumns = Object.entries(finalSelection)
      .filter(([, isSelected]) => isSelected)
      .map(([index]) => tableData[parseInt(index)])
      .map(row => {
        const column = metadataColumns.find((col: Column) => col.header === row.header);
        if (!column) {
          return null; // or handle this case as needed
        }
        return column;
      })
      .filter((column): column is Column => column !== null); // Type guard to ensure no nulls

    setSelectedColumns(newSelectedColumns);
  };

  return (
    <div className="flex flex-col gap-2">
      <Heading size="2" weight="medium" className="text-gray-700">
        Select columns to write to task metadata
      </Heading>
      <div className="flex items-center gap-2">
        <Icon icon={faCircleInfo} className="text-gray-500" size="sm" label="Info" />
        <Text as="p" size="2" className="text-gray-600">
          Sample values from task <Code>{sampleTaskId}</Code>
        </Text>
      </div>

      <DataTable.Root
        className="h-[300px] max-h-[300px] mt-2"
        columns={columns}
        data={tableData}
        loading={isLoading}
        enableRowSelection={true}
        state={{ rowSelection: currentRowSelection }}
        onRowSelectionChange={handleSelectionChange}
        initialState={{
          pagination: {
            pageIndex: 0,
            pageSize: 1000,
          },
        }}
      >
        {({ table }) => (
          <>
            <DataTable.Table>
              <DataTable.Header>
                {table.getHeaderGroups().map(headerGroup => (
                  <DataTable.HeaderGroup key={headerGroup.id}>
                    {headerGroup.headers.map(header => (
                      <DataTable.ColumnHeaderCell key={header.id} header={header} />
                    ))}
                  </DataTable.HeaderGroup>
                ))}
              </DataTable.Header>
              <DataTable.Body>
                {table.getRowModel().rows.map(row => (
                  <DataTable.Row key={row.id} row={row}>
                    {row.getVisibleCells().map(cell => (
                      <DataTable.Cell key={cell.id} cell={cell} />
                    ))}
                  </DataTable.Row>
                ))}
              </DataTable.Body>
            </DataTable.Table>
          </>
        )}
      </DataTable.Root>
    </div>
  );
};
